import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Search,
  Plus,
  Filter,
  Download,
  Loader2,
  AlertTriangle,
  ChevronDown,
  Table,
} from "lucide-react";
import { EventStatusBadge } from "../components/EventApprovalComponents";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import EventQrScanner from "../components/EventQrScanner";

const CollegeEventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
    approvalStatus: "all",
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch events");
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId, comment) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/college/events/${eventId}/approve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment }),
        }
      );

      if (!response.ok) throw new Error("Failed to approve event");
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (eventId, comment) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/college/events/${eventId}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment }),
        }
      );

      if (!response.ok) throw new Error("Failed to reject event");
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const exportToExcel = () => {
    const exportData = events.map((event) => ({
      "Event Title": event.title,
      Type: event.type.charAt(0).toUpperCase() + event.type.slice(1),
      Organizer:
        event.organizer.type === "College"
          ? "College Admin"
          : `${event.organizer.organizedBy.firstName} ${event.organizer.organizedBy.lastName}`,
      "Organizer Type": event.organizer.type,
      Status: event.status,
      "Approval Status": event.approvalDetails.status,
      "Start Date": new Date(event.schedule.startDate).toLocaleDateString(),
      "Start Time": new Date(event.schedule.startDate).toLocaleTimeString(),
      "End Date": new Date(event.schedule.endDate).toLocaleDateString(),
      "End Time": new Date(event.schedule.endDate).toLocaleTimeString(),
      Location: event.venue,
      "Registered Participants": event.participants?.length || 0,
      "Created At": new Date(event.createdAt).toLocaleDateString(),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Events");
    XLSX.writeFile(wb, "events_list.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Events Report", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 23);

    const tableData = events.map((event) => [
      event.title,
      event.type.charAt(0).toUpperCase() + event.type.slice(1),
      event.organizer.type === "College"
        ? "College Admin"
        : `${event.organizer.organizedBy.firstName} ${event.organizer.organizedBy.lastName}`,
      event.status,
      new Date(event.schedule.startDate).toLocaleDateString(),
      event.participants?.length || 0,
    ]);

    doc.autoTable({
      startY: 30,
      head: [["Title", "Type", "Organizer", "Status", "Date", "Participants"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 3 },
    });

    doc.save("events_report.pdf");
  };

  const getEventData = async () => {
    console.log("getEventData");
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title
      .toLowerCase()
      .includes(filters.search.toLowerCase());
    const matchesType = filters.type === "all" || event.type === filters.type;
    const matchesStatus =
      filters.status === "all" || event.status === filters.status;
    const matchesApproval =
      filters.approvalStatus === "all" ||
      event.approvalDetails.status === filters.approvalStatus;

    return matchesSearch && matchesType && matchesStatus && matchesApproval;
  });

  const pendingApprovalCount = events.filter(
    (event) => event.status === "pending_approval"
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="mt-2 text-gray-500">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-x-5">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-gray-900">
                Event Management
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and approve college events
              </p>
            </div>
            <div className="my-3 flex items-center border-2 hover:bg-gray-100 w-fit rounded-md">
              <EventQrScanner afterScanMethod={getEventData} />
            </div>
          </div>

          <div className="mt-4 sm:mt-0 flex space-x-3">
            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg 
                  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        exportToExcel();
                        setShowExportMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Table className="w-4 h-4 mr-2" />
                      Export to Excel
                    </button>
                    <button
                      onClick={() => {
                        exportToPDF();
                        setShowExportMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export to PDF
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate("/college/events/create")}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg 
                shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Event
            </button>
          </div>
        </div>

        {/* Pending Approval Alert */}
        {pendingApprovalCount > 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Pending Approvals
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {pendingApprovalCount} event
                  {pendingApprovalCount === 1 ? "" : "s"} waiting for your
                  approval
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-4 py-2 border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="reunion">Reunion</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
              <option value="other">Other</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="w-full px-4 py-2 border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="published">Published</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={filters.approvalStatus}
              onChange={(e) =>
                setFilters({ ...filters, approvalStatus: e.target.value })
              }
              className="w-full px-4 py-2 border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Approval Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Events Table */}
        <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Event Details
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Organizer
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEvents.map((event) => (
                  <tr key={event._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {event.images?.[0] ? (
                            <img
                              src={event.images[0]}
                              alt=""
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Calendar className="h-5 w-5 text-blue-600" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {event.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {event.type.charAt(0).toUpperCase() +
                              event.type.slice(1)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="text-sm text-gray-900">
                          {event.organizer.type === "College"
                            ? "College Admin"
                            : `${event.organizer.organizedBy.firstName} ${event.organizer.organizedBy.lastName}`}
                        </div>
                        <span
                          className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${
                            event.organizer.type === "College"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {event.organizer.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <EventStatusBadge
                          status={event.status}
                          approvalDetails={event.approvalDetails}
                        />
                        {event.status === "pending_approval" && (
                          <div className="text-xs text-gray-500">
                            Waiting for review
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(
                          event.schedule.startDate
                        ).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(event.schedule.startDate).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        {event.status === "pending_approval" && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleApprove(event._id)}
                              className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium 
                                text-green-700 bg-green-100 hover:bg-green-200"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(event._id)}
                              className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium 
                                text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() =>
                            navigate(`/college/events/${event._id}`)
                          }
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </button>
                        {event.approvalDetails.status === "approved" && (
  <button
    onClick={() => navigate(`/college/events/attendance/${event._id}`)}
    className="text-blue-600 hover:text-blue-900"
  >
    Attendance
  </button>
)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredEvents.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No events found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search ||
                filters.status !== "all" ||
                filters.type !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by creating a new event"}
              </p>
              {!filters.search &&
                filters.status === "all" &&
                filters.type === "all" && (
                  <div className="mt-6">
                    <button
                      onClick={() => navigate("/college/events/create")}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm 
                      text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Create Event
                    </button>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollegeEventList;
